/**
 * CANONICAL MODULE — app-platform/shared/writePrecondition.ts
 *
 * Copied byte-for-byte into every app (`src/lib/writePrecondition.ts`). Its only import is the
 * sibling canonical `graphErrors.ts`. Built for work order W10 (task 2.b) from
 * [design/W10-sync-transport.md](../design/W10-sync-transport.md) §3.4.
 *
 * **Fix bugs HERE, then copy outward. Never edit an app's copy in place.**
 *
 * The contract the CALLER holds: what it knows about a file's version, and what a write may
 * therefore assume. trakr's `WritePrecondition` type, bindr's three-state tag knowledge, bindr's
 * `writeJsonMerging` generalised.
 *
 * ── The one rule ─────────────────────────────────────────────────────────────────────────────────
 * **An unknown tag means read first, never write blind.** `preconditionFor` answers `'read-first'`
 * for it. That single rule closes bindr's unconditional fallback (`graph.ts:359`), contently's
 * unconditional re-PUT after a 404, and trakr's `writeTracked` with no prior read. The writes that
 * are correctly unconditional (per-device files, content-addressed binaries — §2.3) say
 * `{ kind: 'none' }` explicitly, so a blind write is visible in the source, never incidental.
 *
 * ── Which tag ────────────────────────────────────────────────────────────────────────────────────
 * **`cTag` for content `If-Match`, `eTag` for DELETE** (§3.4, open decision 4). `cTag` changes only
 * when the bytes change, so a rename or a OneDrive-client metadata touch does not raise a false 412.
 * `contentTagOf` is the one place that choice is made. Unproven under approot until the pilot's
 * real-Graph check; if Graph rejects it, this is the one-line fallback to `eTag`.
 */

import { DocConflictError } from './graphErrors'

export type WritePrecondition =
  | { kind: 'none' }
  | { kind: 'if-match'; tag: string }
  | { kind: 'expect-absent' }

/** The tag a content precondition should name: `cTag`, else `eTag`, else `null`. */
export function contentTagOf(item: { cTag?: unknown; eTag?: unknown } | null | undefined): string | null {
  if (!item) return null
  if (typeof item.cTag === 'string' && item.cTag) return item.cTag
  if (typeof item.eTag === 'string' && item.eTag) return item.eTag
  return null
}

/** The precondition a write should carry, from what is known: tag → if-match, absent → expect-absent. */
export function preconditionFromTag(tag: string | null): WritePrecondition {
  return tag === null ? { kind: 'expect-absent' } : { kind: 'if-match', tag }
}

export interface TagStore {
  /** `string` = known tag, `null` = seen absent, `undefined` = unknown. */
  get(path: string): string | null | undefined
  set(path: string, tag: string | null): void
  /** Record a folder listing: every child named gets its tag, and any child NOT named is absent. */
  noteListing(folder: string, items: ReadonlyArray<{ name: string; tag: string | null }>): void
  /** Forget everything (sign-out), or everything at or under `prefix` (a delete). */
  forget(prefix?: string): void
  preconditionFor(path: string): WritePrecondition | 'read-first'
}

function trimSlashes(path: string): string {
  return path.replace(/^\/+|\/+$/g, '')
}

function parentOf(path: string): string {
  const i = path.lastIndexOf('/')
  return i < 0 ? '' : path.slice(0, i)
}

function joinPath(folder: string, name: string): string {
  return folder ? `${folder}/${name}` : name
}

function isAtOrUnder(path: string, prefix: string): boolean {
  return prefix === '' || path === prefix || path.startsWith(`${prefix}/`)
}

/**
 * Memory-only knowledge of file versions, keyed by app-relative path (leading/trailing slashes are
 * ignored). bindr's model: a folder whose listing was recorded knows its missing children are absent.
 */
export function createTagStore(): TagStore {
  const tags = new Map<string, string | null>()
  const listedFolders = new Set<string>()

  function get(path: string): string | null | undefined {
    const p = trimSlashes(path)
    if (tags.has(p)) return tags.get(p)
    return listedFolders.has(parentOf(p)) ? null : undefined
  }

  return {
    get,
    set(path, tag) {
      tags.set(trimSlashes(path), tag)
    },
    noteListing(folder, items) {
      const f = trimSlashes(folder)
      // A fresh listing supersedes what was known about this folder's direct children.
      for (const key of [...tags.keys()]) if (parentOf(key) === f) tags.delete(key)
      for (const item of items) tags.set(joinPath(f, item.name), item.tag)
      listedFolders.add(f)
    },
    forget(prefix) {
      if (prefix === undefined) {
        tags.clear()
        listedFolders.clear()
        return
      }
      const p = trimSlashes(prefix)
      for (const key of [...tags.keys()]) if (isAtOrUnder(key, p)) tags.delete(key)
      for (const key of [...listedFolders]) if (isAtOrUnder(key, p)) listedFolders.delete(key)
    },
    preconditionFor(path) {
      const tag = get(path)
      return tag === undefined ? 'read-first' : preconditionFromTag(tag)
    },
  }
}

/**
 * What one write reports: the new content tag, or — additively, since W11 — the transport's
 * `WrittenItem` itself (any object with `tag` and an optional `recreated`), so `recreated` can reach
 * the caller instead of being swallowed. A bare tag is still accepted and means "not recreated".
 */
export type RebasingWriteResult = string | null | { tag: string | null; recreated?: true }

export interface RebasingIo<T> {
  /** The file's current value and content tag; `{ value: null, tag: null }` when it does not exist. */
  read(): Promise<{ value: T | null; tag: string | null }>
  /**
   * Write `value` under `pre`; resolve to the new content tag (or `null` if Graph sent none), or to
   * the `WrittenItem` so `recreated` is reported (see `writeRebasingItem`).
   */
  write(value: T, pre: WritePrecondition): Promise<RebasingWriteResult>
}

/** `writeRebasingItem`'s answer. */
export interface RebasedWrite {
  /** The new content tag. */
  tag: string | null
  /**
   * `true` when the write that landed was an `If-Match` that Graph answered by CREATING the file
   * (`WrittenItem.recreated`): the file had been deleted, or moved, on another device after this
   * device last saw its tag. The write succeeded and is not retried; the caller's orchestration
   * decides whether that deletion should have won (design/W11-sync-orchestration.md §3.4).
   */
  recreated: boolean
  /** How many writes it took (1 = no refusal). */
  writes: number
}

function splitWriteResult(result: RebasingWriteResult): { tag: string | null; recreated: boolean } {
  // Anything that is not an object is a bare tag. That includes `undefined` from an io (or a test
  // double) that resolves to nothing, which the tag-only wrapper passed straight through before W11.
  if (typeof result !== 'object' || result === null) {
    return { tag: typeof result === 'string' ? result : null, recreated: false }
  }
  return { tag: result.tag, recreated: result.recreated === true }
}

/**
 * Write a shared file and, when another device got there first, re-read the fresh copy, re-apply
 * the same change with `rebase`, and try again: at most `maxWrites` writes, then the
 * `DocConflictError` goes out. Any other error goes out at once.
 *
 * Reads first unless `first` says what the caller already knows (a value computed from a copy whose
 * tag it holds). `rebase` must converge rather than compound when re-applied — a union, a single-key
 * set — since it may run against a copy that already contains the change.
 *
 * Reports what happened: the new content tag (for the caller to record), whether the landed write
 * recreated a vanished file, and how many writes it took. A recreated write is a SUCCESS — it is
 * never retried or thrown — but it is passed up, never swallowed (W10 §8.3, W11 §3.4). (There
 * is no tag-only variant: `writeRebasingItem` is the only entry point, and callers read `.tag`.)
 */
export async function writeRebasingItem<T>(
  io: RebasingIo<T>,
  rebase: (fresh: T | null) => T,
  maxWrites = 3,
  first?: { value: T; pre: WritePrecondition },
): Promise<RebasedWrite> {
  let value: T
  let pre: WritePrecondition
  if (first) {
    value = first.value
    pre = first.pre
  } else {
    const fresh = await io.read()
    value = rebase(fresh.value)
    pre = preconditionFromTag(fresh.tag)
  }

  for (let attempt = 1; ; attempt += 1) {
    try {
      return { ...splitWriteResult(await io.write(value, pre)), writes: attempt }
    } catch (error) {
      if (!(error instanceof DocConflictError) || attempt >= maxWrites) throw error
      const fresh = await io.read()
      value = rebase(fresh.value)
      pre = preconditionFromTag(fresh.tag)
    }
  }
}
